import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { submitDeal, uploadJVFile, dealAutofill, resetDealAutofill } from "../../redux/slices/jointVenturesSlice";
import { getEligibleBusinessesThunk } from "../../redux/slices/dealUploadSlice";

import { Form, Input, Upload, Typography, Row, Col, Divider, message } from "antd";
import { DeleteOutlined, PaperClipOutlined, UploadOutlined } from "@ant-design/icons";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { useNavigate, useLocation } from "react-router-dom";
import ReferenceLinksFormList from "../../components/ReferenceLinksFormList/ReferenceLinksFormList";
import {
    formatReferenceLinks,
    parseReferenceLinks,
} from "../../helpers/referenceLinks";
import "./JointVentures.scss";
import CustomButton from "../../components/CustomButton/Button";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import CancelButton from "../../components/CustomButton/CancelButton";
import BackButton from "../../components/CustomButton/BackButton";
import { blockNumberInputWheel } from "../../helpers/helpers";
import { DEAL_CATEGORY_IDS } from "../../constants/constants";

const { Title } = Typography;

const isValidMeaningfulData = (value) => {
    const trimmedValue = String(value || "").trim();
    if (!trimmedValue) return true;

    // Detect excessive repeated characters (5+ consecutive same char)
    if (/(.)(\1{4,})/.test(trimmedValue)) return false;

    // Detect if too many special characters (>40% of length)
    const specialCharCount = (trimmedValue.match(/[^a-zA-Z0-9\s]/g) || []).length;
    if (specialCharCount > trimmedValue.length * 0.4) return false;

    // Detect if mostly just numbers/special chars with minimal letters
    const letterCount = (trimmedValue.match(/[a-zA-Z]/g) || []).length;
    if (letterCount < 2) return false;

    return true;
};

const validateLongTextField = (value, options = {}) => {
    const { required = true, label = "This field" } = options;
    const rawValue = String(value || "");
    const trimmedValue = rawValue.trim();

    if (!trimmedValue) {
        return required ? Promise.reject(new Error(`Please enter ${label}`)) : Promise.resolve();
    }

    if (!isValidMeaningfulData(trimmedValue)) return Promise.reject(new Error("Please enter a valid and meaningful data"));
    if (trimmedValue.length < 4) return Promise.reject(new Error(`${label} must be at least 4 characters`));
    if (rawValue.length > 500) return Promise.reject(new Error(`${label} cannot exceed 500 characters`));
    if (/<[^>]*>/.test(trimmedValue)) return Promise.reject(new Error(`${label} cannot contain HTML or script tags`));
    if (!/[A-Za-z]/.test(trimmedValue)) return Promise.reject(new Error(`${label} must contain at least one alphabet`));

    return Promise.resolve();
};

const normalizeAmountInput = (value) => {
    if (value === undefined || value === null || value === "") return "";
    return String(value).replace(/[^0-9]/g, "");
};

const formatAmountInput = (value) => {
    const normalized = normalizeAmountInput(value);
    if (!normalized) return "";
    return Number(normalized).toLocaleString("en-IN");
};

const toAutofillNumber = (value) => {
    if (value === undefined || value === null || value === "") return null;
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
};

const buildUploadedFile = (url, uid = "-1") => {
    if (!url) return null;

    return {
        uid,
        name: decodeURIComponent(String(url).split("/").pop()),
        status: "done",
        url,
        response: { url },
    };
};

const JV_AUTOFILL_FIELD_KEYS = [
    "projectName",
    "projectDescription",
    "projectDuration",
    "projectValue",
    "fundRequired",
    "expectedRoi",
    "profitShare",
    "investmentStructure",
    "profitSharingModel",
    "riskFactors",
    "referenceLinks",
    "projectProposal",
    "pitchDeck",
    "financialProjections",
    "supportingDocs",
    "amountToRaise",
    "dealDetails",
    "otherTermsConditions",
];

const hasJvDealAutofillPayload = (payload) => {
    if (!payload || typeof payload !== "object") return false;
    return JV_AUTOFILL_FIELD_KEYS.some(
        (key) => payload[key] != null && payload[key] !== "",
    );
};

const applyAutofillToForm = (autofillData, form, setters) => {
    if (!autofillData) return;

    const {
        setProjectProposalFileList,
        setFinancialProjectionsFileList,
        setSupportingDocsFileList,
    } = setters;

    const formValues = {};

    [
        "projectName",
        "projectDescription",
        "investmentStructure",
        "profitSharingModel",
        "riskFactors",
    ].forEach((field) => {
        const value = autofillData[field]
            ?? (field === "projectDescription" ? autofillData.dealDetails : undefined)
            ?? (field === "riskFactors" ? autofillData.otherTermsConditions : undefined);
        if (value != null && value !== "") {
            formValues[field] = value;
        }
    });

    [
        "projectDuration",
        "projectValue",
        "fundRequired",
        "expectedRoi",
        "profitShare",
    ].forEach((field) => {
        const value = autofillData[field]
            ?? (field === "fundRequired" ? autofillData.amountToRaise : undefined);
        const numeric = toAutofillNumber(value);
        if (numeric != null) {
            formValues[field] = numeric;
        }
    });

    if (autofillData.referenceLinks) {
        formValues.otherLinks = parseReferenceLinks(autofillData.referenceLinks);
    }

    form.setFieldsValue(formValues);

    const projectProposalUrl = autofillData.projectProposal || autofillData.pitchDeck;
    const projectProposalFile = buildUploadedFile(projectProposalUrl, "autofill-proposal");
    if (projectProposalFile) {
        setProjectProposalFileList([projectProposalFile]);
        form.setFieldValue("projectProposal", [projectProposalFile]);
    }

    const financialProjectionsFile = buildUploadedFile(
        autofillData.financialProjections,
        "autofill-financial"
    );
    if (financialProjectionsFile) {
        setFinancialProjectionsFileList([financialProjectionsFile]);
        form.setFieldValue("financialProjections", [financialProjectionsFile]);
    }

    const supportingDocsFile = buildUploadedFile(
        autofillData.supportingDocs,
        "autofill-supporting"
    );
    if (supportingDocsFile) {
        setSupportingDocsFileList([supportingDocsFile]);
        form.setFieldValue("supportingDocs", [supportingDocsFile]);
    }
};


const JointVentures = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [projectProposalFileList, setProjectProposalFileList] = useState([]);
    const [financialProjectionsFileList, setFinancialProjectionsFileList] = useState([]);
    const [supportingDocsFileList, setSupportingDocsFileList] = useState([]);
    const [uploadingFields, setUploadingFields] = useState({ projectProposal: false, financialProjections: false, supportingDocs: false });
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const { dealAutofillList, error } = useSelector((state) => state.jointVentures);
    const { businessList, loading: businessLoading } = useSelector((state) => state?.dealUpload || {});

    // Get navigation state passed via react-router state (e.g., from DealsManagement)
    const commonState = location?.state || {};

    // Derive the active businessId: prefer location state, fall back to picker selection
    const activeBusinessId = commonState?.businessId || selectedBusiness;
    const activeBusinessName = commonState?.businessName ||
        businessList?.find((b) => b.orgId === selectedBusiness)?.orgName || null;

    // Fetch eligible JV businesses when no businessId is pre-supplied via navigation state
    useEffect(() => {
        if (!commonState?.businessId) {
            dispatch(getEligibleBusinessesThunk());
        }
    }, [dispatch, commonState?.businessId]);

    useEffect(() => {
        form.resetFields();
        setProjectProposalFileList([]);
        setFinancialProjectionsFileList([]);
        setSupportingDocsFileList([]);

        if (!activeBusinessId) {
            dispatch(resetDealAutofill());
            return undefined;
        }

        let cancelled = false;
        dispatch(
            dealAutofill({
                orgId: activeBusinessId,
                dealId: commonState.dealId,
            }),
        ).then((result) => {
            if (cancelled || result?.meta?.requestStatus !== "fulfilled") return;
            if (!hasJvDealAutofillPayload(result.payload)) return;
            applyAutofillToForm(result.payload, form, {
                setProjectProposalFileList,
                setFinancialProjectionsFileList,
                setSupportingDocsFileList,
            });
        });

        return () => {
            cancelled = true;
        };
        // form instance is stable from Form.useForm(); omit from deps to avoid re-fetch loops
    }, [dispatch, activeBusinessId, commonState?.dealId, form]);

    // Upload handler using Redux thunk — runs on every file selection (first time included)
    const uploadSelectedFile = async (fieldName, file) => {
        const folder = `business/${activeBusinessId}`;
        setUploadingFields(prev => ({ ...prev, [fieldName]: true }));
        try {
            const result = await dispatch(uploadJVFile({ file, folderPath: folder }));
            if (result?.meta?.requestStatus === 'fulfilled') {
                const { fileUrl, fileName } = result.payload;
                const uploadedItem = {
                    uid: file.uid,
                    name: fileName || file.name,
                    status: "done",
                    response: { url: fileUrl, fileUrl },
                };

                handleFileChange(fieldName, [uploadedItem]);
                message.success(`${fileName || file.name} uploaded successfully`);
            } else {
                const errMsg = result?.payload || "Upload failed";
                message.error(`Failed to upload ${file.name}: ${errMsg}`);
            }
        } catch (err) {
            message.error(`Failed to upload ${file.name}. Please try again.`);
        } finally {
            setUploadingFields(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const handleBeforeUpload = (fieldName) => async (file) => {
        const fileName = file.name.toLowerCase();
        // Validation rules
        if (fieldName === "projectProposal") {
            if (!fileName.endsWith(".pdf")) {
                message.error("Only PDF (.pdf) files are allowed for Upload Your Proposal");
                return Upload.LIST_IGNORE;
            }
        }
        if (fieldName === "financialProjections") {
            if (
                !fileName.endsWith(".pdf") &&
                !fileName.endsWith(".xls") &&
                !fileName.endsWith(".xlsx") &&
                !fileName.endsWith(".csv")
            ) {
                message.error("Only PDF, Excel (.xls, .xlsx), and CSV files are allowed for Financial Projections");
                return Upload.LIST_IGNORE;
            }
        }
        // Supporting Docs → no restriction
        await uploadSelectedFile(fieldName, file);
        return Upload.LIST_IGNORE;
    };

    const handleFileChange = (fieldName, fileList = []) => {
        const latestFile = [...fileList].reverse().find((item) => item?.status !== "removed");
        const normalized = latestFile ? [latestFile] : [];
        form.setFieldValue(fieldName, normalized);
        if (fieldName === "projectProposal") setProjectProposalFileList(normalized);
        if (fieldName === "financialProjections") setFinancialProjectionsFileList(normalized);
        if (fieldName === "supportingDocs") setSupportingDocsFileList(normalized);
    };

    const clearSingleFile = (fieldName) => {
        handleFileChange(fieldName, []);
    };

    const handleFinish = async (values) => {
        if (!activeBusinessId) {
            message.error('Please select a business');
            return;
        }
        setLoading(true);
        try {
            const getUploadedUrl = (fileItem) =>
                fileItem?.response?.url || fileItem?.response?.fileUrl || fileItem?.url || null;

            // Extract uploaded file URLs
            const projectProposalUrl = getUploadedUrl(values.projectProposal?.[0]);
            const financialProjectionsUrl = getUploadedUrl(values.financialProjections?.[0]);
            const supportingDocsUrls = getUploadedUrl(values.supportingDocs?.[0]);

            if (!projectProposalUrl) {
                message.error("Upload Your Proposal upload is not complete. Please wait and try again.");
                setLoading(false);
                return;
            }

            if (!financialProjectionsUrl) {
                message.error("Financial Projections upload is not complete. Please wait and try again.");
                setLoading(false);
                return;
            }

            const payload = {
                projectName: (values.projectName || "").trim(),
                projectDescription: (values.projectDescription || "").trim(),
                projectValue: values.projectValue,
                projectDuration: values.projectDuration,
                fundRequired: values.fundRequired,
                expectedRoi: values.expectedRoi,
                profitShare: values.profitShare,
                investmentStructure: (values.investmentStructure || "").trim(),
                profitSharingModel: (values.profitSharingModel || "").trim(),
                riskFactors: values.riskFactors ? values.riskFactors.trim() : null,
                referenceLinks: formatReferenceLinks(values.otherLinks),
                orgId: activeBusinessId,
                projectProposal: projectProposalUrl || null,
                financialProjections: financialProjectionsUrl || null,
                supportingDocs: supportingDocsUrls || null,
            };

            const res = await dispatch(submitDeal(payload));
            if (res?.payload?.statusCode === 201) {
                message.success({
                    content: res?.payload?.response?.message || "Deal submitted successfully!",
                    duration: 3,
                    style: { marginTop: '60px', textAlign: 'center' },
                });
                form.resetFields();
                setProjectProposalFileList([]);
                setFinancialProjectionsFileList([]);
                setSupportingDocsFileList([]);
                dispatch(resetDealAutofill());
                navigate("/deals-management", { state: { activeTab: "joint-ventures-deals" } });
            }
        } catch (err) {
            // handle error
        }
        setLoading(false);
    };

    const autofillDealDocumentUrl =
        dealAutofillList?.projectProposal || dealAutofillList?.pitchDeck;

    // Filtered JV business options — only active businesses eligible for JV deals
    const jvBusinessOptions = (businessList || [])
        .filter((item) => {
            const statusVal = item.statusId || item.businessStatusId || item.orgStatusId;
            if (statusVal && statusVal !== "USM1000001") return false;
            const statusName = (item.statusName || item.status || "").toLowerCase();
            if (statusName && statusName !== "active" && !statusName.includes("active")) return false;
            const categories = item.dealCategory || [];
            const hasJv = categories.some(
                (cat) => cat?.dealCategoryId === DEAL_CATEGORY_IDS.JOINT_VENTURES || (cat?.dealCategoryName || "").toLowerCase().includes("joint venture")
            );
            return hasJv;
        })
        .map((item) => ({ value: item.orgId, label: item.orgName }));

    return (
        <div className="joint-ventures-container">
            <BackButton to="/deals-management" />
            <div className="deal-header">
                <div className="deal-title">Joint Venture - Deal Upload</div>
            </div>

            {/* Business selector — only shown when not pre-supplied via navigation state */}
            {!commonState?.businessId && (
                <div className="jv-business-selector">
                    <label className="jv-business-label">
                        Select Business <span className="required-star">*</span>
                    </label>
                    <CustomSelect
                        showSearch
                        placeholder={businessLoading ? "Loading..." : "-- Select Business --"}
                        className="custom-ent-select jv-business-select"
                        loading={businessLoading}
                        disabled={businessLoading}
                        options={jvBusinessOptions}
                        value={selectedBusiness}
                        onChange={(value) => {
                            setSelectedBusiness(value);
                            form.resetFields();
                            setProjectProposalFileList([]);
                            setFinancialProjectionsFileList([]);
                            setSupportingDocsFileList([]);
                        }}
                    />
                    {!businessLoading && jvBusinessOptions.length === 0 && (
                        <div className="jv-empty-text">No active Joint Venture businesses available</div>
                    )}
                </div>
            )}

            <div className="deal-business"><b>Business: </b>{activeBusinessName || 'N/A'}</div>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                className="joint-ventures-form"
            >
                <div className="space-around">
                    <Title level={5} className="section-title">Basic Deal Information</Title>
                    <Row gutter={16}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                label="Project Name"
                                name="projectName"
                                rules={[
                                    { required: true, message: "Please enter project name" },
                                    {
                                        validator: (_, value) => {
                                            if (!value) return Promise.resolve();
                                            const trimmedValue = String(value).trim();
                                            if (!trimmedValue) return Promise.reject(new Error("Please enter project name"));
                                            if (!isValidMeaningfulData(trimmedValue)) return Promise.reject(new Error("Please enter a valid and meaningful data"));
                                            if (trimmedValue.length < 3) return Promise.reject(new Error("Project name must be at least 3 characters"));
                                            if (trimmedValue.length > 100) return Promise.reject(new Error("Project name cannot exceed 100 characters"));
                                            if (!/[A-Za-z]/.test(trimmedValue)) {
                                                return Promise.reject(new Error("Project name must contain at least one alphabet"));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input placeholder="Enter project name" maxLength={100} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={24}>
                            <Form.Item
                                label="Project Description"
                                name="projectDescription"
                                rules={[
                                    { required: true, message: "Please provide detailed project description" },
                                    {
                                        validator: (_, value) => {
                                            if (!value) return Promise.resolve();

                                            const rawValue = String(value);
                                            const trimmedValue = rawValue.trim();

                                            if (!trimmedValue) return Promise.reject(new Error("Please provide detailed project description"));
                                            if (!isValidMeaningfulData(trimmedValue)) return Promise.reject(new Error("Please enter a valid and meaningful data"));
                                            if (trimmedValue.length < 4) return Promise.reject(new Error("Project description must be at least 4 characters"));
                                            if (rawValue.length > 500) return Promise.reject(new Error("Project description cannot exceed 500 characters"));
                                            if (/<[^>]*>/.test(trimmedValue)) return Promise.reject(new Error("Project description cannot contain HTML or script tags"));
                                            if (!/[A-Za-z]/.test(trimmedValue)) return Promise.reject(new Error("Project description must contain at least one alphabet"));

                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input.TextArea placeholder="Provide detailed project description" autoSize={{ minRows: 2, maxRows: 4 }} maxLength={500} showCount />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Project Duration (Months)"
                                name="projectDuration"
                                rules={[
                                    { required: true, message: "Please enter project duration" },
                                    {
                                        validator: (_, value) => {
                                            if (value === undefined || value === null || value === "") return Promise.resolve();
                                            const num = Number(value);
                                            if (num < 1) return Promise.reject(new Error("Project duration must be at least 1 month"));
                                            if (num > 200) return Promise.reject(new Error("Project duration should not exceed 200 months"));
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input placeholder="e.g., 24" type="number" min={1} onWheel={blockNumberInputWheel} onKeyDown={(e) => { if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault(); }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Project Value (₹)"
                                name="projectValue"
                                normalize={(value) => normalizeAmountInput(value)}
                                getValueProps={(value) => ({ value: formatAmountInput(value) })}
                                rules={[
                                    { required: true, message: "Please enter project value" },
                                    {
                                        validator: (_, value) => {
                                            if (value === undefined || value === null || value === "") return Promise.resolve();
                                            const num = Number(value);
                                            if (num < 200000) return Promise.reject(new Error("Project value must be at least ₹2,00,000"));
                                            if (num > 100000000000000) return Promise.reject(new Error("Project value cannot exceed ₹1,00,00,00,00,00,000"));
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="e.g., 50,00,000"
                                    type="text"
                                    inputMode="numeric"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Fund Required (₹)"
                                name="fundRequired"
                                dependencies={["projectValue"]}
                                normalize={(value) => normalizeAmountInput(value)}
                                getValueProps={(value) => ({ value: formatAmountInput(value) })}
                                rules={[
                                    { required: true, message: "Please enter fund required" },
                                    {
                                        validator: (_, value) => {
                                            if (value === undefined || value === null || value === "") return Promise.resolve();
                                            const projectValue = form.getFieldValue("projectValue");
                                            if (Number(value) < 200000) {
                                                return Promise.reject(new Error("Fund required must be at least ₹2,00,000"));
                                            }
                                            if (projectValue && Number(value) > Number(projectValue)) {
                                                return Promise.reject(new Error("Fund required should not exceed project value"));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="e.g., 20,00,000"
                                    type="text"
                                    inputMode="numeric"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Expected ROI (%)"
                                name="expectedRoi"
                                rules={[
                                    { required: true, message: "Please enter expected ROI" },
                                    {
                                        validator: (_, value) => {
                                            if (value === undefined || value === null || value === "") return Promise.resolve();
                                            const num = Number(value);
                                            if (num <= 0) return Promise.reject(new Error("ROI must be greater than 0"));
                                            if (num >= 100) return Promise.reject(new Error("ROI should be less than 100%"));
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input placeholder="e.g., 18.5" type="number" min={0} step={0.1} onWheel={blockNumberInputWheel} onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Profit Share (%)"
                                name="profitShare"
                                rules={[
                                    { required: true, message: "Please enter profit share" },
                                    {
                                        validator: (_, value) => {
                                            if (value === undefined || value === null || value === "") return Promise.resolve();
                                            const num = Number(value);
                                            if (num < 0) return Promise.reject(new Error("Profit share cannot be negative"));
                                            if (num > 100) return Promise.reject(new Error("Profit share should not exceed 100%"));
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input placeholder="e.g., 30" type="number" min={0} step={0.1} onWheel={blockNumberInputWheel} onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
                <Divider className="section-divider" />
                <div className="space-around">
                    <Title level={5} className="section-title">Additional Details</Title>
                    <Row gutter={16}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                label="Investment Structure"
                                name="investmentStructure"
                                required
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            return validateLongTextField(value, {
                                                required: true,
                                                label: "Investment structure",
                                            });
                                        },
                                    },
                                ]}
                            >
                                <Input.TextArea placeholder="Describe the investment structure (e.g., equity, debt, hybrid)" autoSize={{ minRows: 2, maxRows: 4 }} maxLength={500} showCount />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={24}>
                            <Form.Item
                                label="Revenue / Profit Sharing Model"
                                name="profitSharingModel"
                                required
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            return validateLongTextField(value, {
                                                required: true,
                                                label: "Revenue / profit sharing model",
                                            });
                                        },
                                    },
                                ]}
                            >
                                <Input.TextArea placeholder="Describe the revenue or profit sharing model" autoSize={{ minRows: 2, maxRows: 4 }} maxLength={500} showCount />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={24}>
                            <Form.Item
                                label="Risk Factors (Optional)"
                                name="riskFactors"
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            return validateLongTextField(value, {
                                                required: false,
                                                label: "Risk factors",
                                            });
                                        },
                                    },
                                ]}
                            >
                                <Input.TextArea placeholder="Describe any potential risks associated with this project" autoSize={{ minRows: 2, maxRows: 4 }} maxLength={500} showCount />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <ReferenceLinksFormList />
                        </Col>
                    </Row>
                </div>
                <Divider className="section-divider" />
                <div className="space-around">
                    <Title level={5} className="section-title">Document Uploads</Title>
                    <Row gutter={16}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                label="Upload Your Proposal"
                                name="projectProposal"
                                valuePropName="fileList"
                                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                                rules={[{ required: true, message: "Please upload Upload Your Proposal" }]}
                            >
                                {autofillDealDocumentUrl ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            padding: "12px",
                                            border: "1px solid #d9d9d9",
                                            borderRadius: "6px",
                                            background: "#fafafa",
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                            }}
                                        >
                                            <PaperClipOutlined />
                                            {decodeURIComponent(
                                                autofillDealDocumentUrl.split("/").pop()
                                            )}
                                        </span>

                                        <a
                                            href={autofillDealDocumentUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            View Document
                                        </a>
                                    </div>
                                ) : (
                                    <Upload.Dragger
                                        name="files"
                                        beforeUpload={handleBeforeUpload("projectProposal")}
                                        multiple={false}
                                        maxCount={1}
                                        accept=".pdf"
                                        showUploadList={false}
                                        disabled={uploadingFields.projectProposal}
                                    >
                                        <p className="ant-upload-drag-icon">
                                            <UploadOutlined />
                                        </p>
                                        <p>
                                            {uploadingFields.projectProposal
                                                ? "Uploading..."
                                                : "Click to upload Upload Your Proposal (.pdf)"}
                                        </p>
                                    </Upload.Dragger>
                                )}
                            </Form.Item>

                            {!autofillDealDocumentUrl && projectProposalFileList?.[0] ? (
                                <div
                                    style={{
                                        marginTop: 8,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <span
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }}
                                    >
                                        <PaperClipOutlined />
                                        {projectProposalFileList[0]?.name}
                                    </span>

                                    <DeleteOutlined
                                        style={{ cursor: "pointer" }}
                                        onClick={() => clearSingleFile("projectProposal")}
                                    />
                                </div>
                            ) : null}
                        </Col>
                        <Col xs={24} md={24}>
                            <Form.Item
                                label="Financial Projections"
                                name="financialProjections"
                                valuePropName="fileList"
                                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                                rules={[{ required: true, message: "Please upload financial projections (.pdf,.xls,.xlsx, .csv)" }]}
                            >
                                <Upload.Dragger
                                    name="files"
                                    beforeUpload={handleBeforeUpload("financialProjections")}
                                    multiple={false}
                                    maxCount={1}
                                    accept=".pdf,.csv"
                                    showUploadList={false}
                                    disabled={uploadingFields.financialProjections}
                                >
                                    <p className="ant-upload-drag-icon">
                                        <UploadOutlined />
                                    </p>
                                    <p>{uploadingFields.financialProjections ? "Uploading..." : "Click to upload financial projections (.pdf,.xls,.xlsx, .csv)"}</p>
                                </Upload.Dragger>
                            </Form.Item>
                            {financialProjectionsFileList?.[0] ? (
                                <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <PaperClipOutlined />
                                        {financialProjectionsFileList[0]?.name}
                                    </span>
                                    <DeleteOutlined style={{ cursor: "pointer" }} onClick={() => clearSingleFile("financialProjections")} />
                                </div>
                            ) : null}
                        </Col>
                        <Col xs={24} md={24}>
                            <Form.Item
                                label="Supporting Documents (Optional)"
                                name="supportingDocs"
                                valuePropName="fileList"
                                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                            >
                                <Upload.Dragger
                                    name="files"
                                    beforeUpload={handleBeforeUpload("supportingDocs")}
                                    multiple={false}
                                    maxCount={1}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv"
                                    showUploadList={false}
                                    disabled={uploadingFields.supportingDocs}
                                >
                                    <p className="ant-upload-drag-icon">
                                        <UploadOutlined />
                                    </p>
                                    <p>{uploadingFields.supportingDocs ? "Uploading..." : "Click to upload additional documents (.pdf, .doc, .docx, .xls, .xlsx, .csv)"}</p>
                                </Upload.Dragger>
                            </Form.Item>
                            {supportingDocsFileList?.[0] ? (
                                <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <PaperClipOutlined />
                                        {supportingDocsFileList[0]?.name}
                                    </span>
                                    <DeleteOutlined style={{ cursor: "pointer" }} onClick={() => clearSingleFile("supportingDocs")} />
                                </div>
                            ) : null}
                        </Col>
                    </Row>
                </div>
                {error && <ErrorAlert error={error} />}

                <div className="form-actions space-around">
                    <CancelButton className="cancel-btn" onClick={() => navigate("/deals-management", { state: { activeTab: "joint-ventures-deals" } })}>Cancel</CancelButton>
                    <CustomButton type="primary" htmlType="submit" loading={loading} className="submit-btn">
                        Submit Deal
                    </CustomButton>
                </div>
            </Form>
        </div>
    );
};

export default JointVentures;
